/**
 * What setup and Settings check as people type, in one place so both say the same thing: a sending address, an
 * email, a web address, and the shape of each tracking ID.
 */

/** Forgive what people paste (scheme, path, port, trailing dot, stray spaces). */
export function cleanDomain(input: string): string {
  return input
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.+$/, "");
}

const DOMAIN = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

export const isDomain = (v: string): boolean => DOMAIN.test(v);

/** A web address with nothing in front, like mayatorres.com: sending from it would disturb the business's own mail. */
const isBare = (v: string): boolean => v.split(".").filter(Boolean).length <= 2;

/** The domain a sending address sits under: mail.mayatorres.com is under mayatorres.com. */
export function rootOf(domain: string): string {
  const labels = domain.split(".");
  return labels.length > 2 ? labels.slice(1).join(".") : domain;
}

type SendingNote =
  | { kind: "hint" }
  | { kind: "bad" }
  | { kind: "bare"; domain: string; suggestion: string }
  | { kind: "good" };

/** What the sending address field says under itself as it is typed. */
export function sendingNote(input: string): SendingNote {
  const v = cleanDomain(input);
  if (!v) return { kind: "hint" };
  if (!isDomain(v)) return { kind: "bad" };
  if (isBare(v)) return { kind: "bare", domain: v, suggestion: `mail.${v}` };
  return { kind: "good" };
}

export const sendingOk = (input: string): boolean => sendingNote(input).kind === "good";

export const looksLikeEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(v.trim());

/** The part before the @ of a from address, as typed: letters, digits, dots, dashes and underscores. */
export const cleanLocal = (v: string): string => v.replace(/[^a-z0-9._-]/gi, "");

/** A free web address's name: letters, numbers and dashes, never starting or ending in a dash. */
export const slugOk = (v: string): boolean => /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(v);

export type TrackKind = "meta" | "ga" | "own";

export const TRACK_NAMES: Record<TrackKind, string> = { meta: "Meta pixel", ga: "Google Analytics", own: "Your own script" };

/** What is wrong with a tracking ID or pasted code, or "" when its shape is right. */
export function trackProblem(kind: TrackKind, value: string): string {
  if (kind === "meta") {
    const id = value.replace(/\s/g, "");
    if (!id) return "Paste your Pixel ID first.";
    if (!/^\d+$/.test(id)) return "A Pixel ID is only digits, like 104822103391557.";
    if (id.length < 15 || id.length > 16) return `A Pixel ID is 15 or 16 digits. This one has ${id.length}.`;
    return "";
  }
  if (kind === "ga") {
    const id = value.trim();
    if (!id) return "Paste your Measurement ID first.";
    if (!/^G-[A-Z0-9]{6,16}$/i.test(id)) return "A Measurement ID starts with G- and then letters and digits, like G-7KQ2MT9WXR.";
    return "";
  }
  if (!value.trim()) return "Paste the code first.";
  if (!/<script[\s>]/i.test(value)) return "Paste the whole code, starting with <script>.";
  return "";
}

/** A record's name as a DNS host asks for it: without the domain on the end, which the host adds by itself. */
export function recordName(name: string, root: string): string {
  const n = name.replace(/\.$/, "");
  if (n === root) return "@";
  return n.endsWith(`.${root}`) ? n.slice(0, -(root.length + 1)) : n;
}

/** The MX record's priority, which goes in its own box at every host. */
export const MX_PRIORITY = "10";

// Why these: an address at a free mail service says nothing about the business's own domain.
const FREE_MAIL = new Set(["gmail.com", "googlemail.com", "yahoo.com", "outlook.com", "hotmail.com", "live.com", "icloud.com", "me.com", "aol.com", "proton.me", "protonmail.com", "gmx.com"]);

/** The business's own domain as far as setup can tell: its sending address's, else the person's work email's. */
export function ownDomainGuess(sendingDomain: string | null, email: string | undefined): string | null {
  if (sendingDomain) return rootOf(sendingDomain);
  const at = (email ?? "").split("@")[1]?.toLowerCase() ?? "";
  return at && !FREE_MAIL.has(at) && isDomain(at) ? at : null;
}
